import type { Widget } from '@dashboard/types/config'
import type { PreviewDevice, PreviewScheme } from '../../../composables/useWidgetEditorContext'
import { defaultWidgetOptions, IMAGE_THEMES, TYPE_SCALE_PRESETS } from '../../../data/widgetTypeDefinitions'
import type { WidgetType } from '../../../data/widgetTypeDefinitions'

export type Options = Widget['options']
const defaultsByType: Record<WidgetType, Options> = {
  modal: defaultWidgetOptions('modal'),
  takeover: defaultWidgetOptions('takeover'),
  toaster: defaultWidgetOptions('toaster'),
  bar: defaultWidgetOptions('bar'),
  inline: defaultWidgetOptions('inline')
}
export type Value = string | number | boolean | null
export interface Choice {
  label: string
  value: Value
}
export interface DesignField {
  path: string
  label: string
  kind: 'select' | 'segments' | 'slider' | 'toggle' | 'text' | 'color' | 'font' | 'image' | 'css'
  choices?: Choice[]
  min?: number
  max?: number
  step?: number
  unit?: string
  fallback?: Value
  inheritFrom?: string
  help?: string
  types?: WidgetType[]
  imageTheme?: boolean
  when?: [string, Value]
  customScale?: boolean
  alsoWhen?: [string, Value]
  // Theme default shown when the stored value is null, per widget type.
  typeFallback?: Partial<Record<WidgetType, Value>>
}
export interface DesignSection {
  id: string
  title: string
  fields: DesignField[]
}
const choices = (values: string[]): Choice[] =>
  values.map((value) => ({ label: value.charAt(0).toUpperCase() + value.slice(1), value }))
const segments = (path: string, label: string, values: string[], fallback?: Value): DesignField => ({
  path,
  label,
  kind: 'segments',
  choices: choices(values),
  fallback
})
const slider = (
  path: string,
  label: string,
  max: number,
  fallback: Value = '0px',
  unit = 'px',
  min = 0,
  step = 1
): DesignField => ({ path, label, kind: 'slider', max, min, step, fallback, unit })
const weights = [400, 500, 600, 700, 800, 900].map((value) => ({ label: String(value), value }))
const headerTypes: WidgetType[] = ['modal', 'toaster', 'inline']
const backdropTypes: WidgetType[] = ['modal', 'takeover']
// Bars lay content out in a row, so Custom density and its vertical spacing exclude them.
const spacedTypes: WidgetType[] = ['modal', 'takeover', 'toaster', 'inline']
const stepTransitionChoices: Choice[] = [
  { label: 'Slide left', value: 'slideLeft' },
  { label: 'Slide up', value: 'slideUp' },
  { label: 'Slide down', value: 'slideDown' },
  { label: 'Cross-fade', value: 'crossfade' },
  { label: 'Zoom', value: 'zoom' },
  { label: 'None', value: 'none' }
]
// Fallbacks match serializeWidgetCss in resources/js/Snippet/funnel/widgetDesign.js.
const spacing = (path: string, label: string, max: number, fallback: string): DesignField => ({
  ...slider(path, label, max, fallback),
  types: spacedTypes,
  when: ['look.density', 'custom']
})
const customSpacingFields: DesignField[] = [
  spacing('structure.containerPaddingY', 'Vertical padding', 120, '35px'),
  spacing('structure.containerPaddingX', 'Horizontal padding', 120, '35px'),
  {
    ...spacing('structure.headerSpacing', 'Header to content', 120, '35px'),
    types: headerTypes,
    alsoWhen: ['headerInfo.isEnabled', true]
  },
  spacing('structure.titleSpacing', 'Heading to description', 80, '9px'),
  spacing('structure.contentSpacing', 'Description to answers & fields', 80, '26px'),
  spacing('structure.submitSpacing', 'Fields to buttons', 80, '8px')
]
export const backgroundFields: DesignField[] = [
  segments('look.backgroundType', 'Background', ['color', 'gradient', 'image'], 'color'),
  { path: 'palette.backgroundColor', label: 'Background color', kind: 'color', when: ['look.backgroundType', 'color'] },
  { path: 'palette.backgroundGradientFrom', label: 'Start', kind: 'color', when: ['look.backgroundType', 'gradient'] },
  { path: 'palette.backgroundGradientTo', label: 'End', kind: 'color', when: ['look.backgroundType', 'gradient'] },
  { ...slider('look.backgroundGradientAngle', 'Angle', 360, 135, '°'), when: ['look.backgroundType', 'gradient'] },
  {
    path: 'look.backgroundImageUrl',
    label: 'Background image',
    kind: 'image',
    when: ['look.backgroundType', 'image'],
    help: 'The same image is used in light and dark mode'
  },
  { path: 'palette.backgroundColor', label: 'Behind the image', kind: 'color', when: ['look.backgroundType', 'image'] }
]
// The base palette comes first without a heading; the other groups refine parts of the widget.
export const colorGroups = [
  {
    id: 'base',
    fields: [
      { path: 'palette.textOnBackgroundColor', label: 'Text' },
      { path: 'palette.contrastColor', label: 'Submit button' },
      { path: 'palette.textOnContrastColor', label: 'Text on buttons' },
      { path: 'palette.borderColor', label: 'Border' }
    ]
  },
  {
    id: 'answers',
    label: 'Question colors',
    fields: [
      { path: 'palette.questionHeadingColor', label: 'Heading text' },
      { path: 'palette.questionTextColor', label: 'Body text' },
      { path: 'palette.answerButtonColor', label: 'Answer background' },
      { path: 'palette.textOnAnswerButtonColor', label: 'Answer text' }
    ]
  },
  {
    id: 'fields',
    label: 'Offer / form colors',
    fields: [
      { path: 'palette.offerHeadingColor', label: 'Heading text' },
      { path: 'palette.offerTextColor', label: 'Body text' },
      { path: 'palette.formFieldColor', label: 'Field background' },
      { path: 'palette.formFieldTextColor', label: 'Field text' },
      { path: 'palette.formFieldBorderColor', label: 'Field border' },
      { path: 'palette.placeholderColor', label: 'Placeholder' },
      { path: 'palette.linkColor', label: 'Links' }
    ]
  },
  {
    id: 'header',
    label: 'Header',
    fields: [
      { path: 'palette.headerTitleColor', label: 'Title', types: headerTypes, when: ['headerInfo.isEnabled', true] },
      {
        path: 'palette.headerSubtitleColor',
        label: 'Subtitle',
        types: headerTypes,
        when: ['headerInfo.isEnabled', true]
      }
    ]
  },
  {
    id: 'backdrop',
    label: 'Backdrop behind the popup',
    fields: [{ path: 'palette.backdropColor', label: 'Backdrop', types: backdropTypes }]
  }
] satisfies { id: string; label?: string; fields: Pick<DesignField, 'path' | 'label' | 'types' | 'when'>[] }[]
// Each size follows the Small/Medium/Large preset; editing one switches the type scale to Custom.
export const sizeFields: DesignField[] = [
  ...(
    [
      ['headlineFontSize', 'Headline', 72],
      ['descriptionFontSize', 'Description', 40],
      ['answerButtonFontSize', 'Answers', 40],
      ['formFieldFontSize', 'Form fields', 40],
      ['submitButtonFontSize', 'Submit button', 40]
    ] as const
  ).map(([key, label, max]) => ({ ...slider(`fontSize.${key}`, label, max, null, 'px', 8), customScale: true })),
  // Widgets saved before header sizes existed keep the theme's rem size (rounded at a 16px root) until a scale is picked.
  ...[
    {
      ...slider('fontSize.headerTitleFontSize', 'Header title', 40, null, 'px', 8),
      typeFallback: { modal: '16px', inline: '15px', toaster: '14px' }
    },
    {
      ...slider('fontSize.headerSubtitleFontSize', 'Header subtitle', 32, null, 'px', 8),
      typeFallback: { modal: '14px', inline: '13px', toaster: '12px' }
    }
  ].map((field) => ({
    ...field,
    customScale: true,
    types: headerTypes,
    when: ['headerInfo.isEnabled', true] satisfies [string, Value]
  }))
]
export const buttonGroups: { label: string; fields: DesignField[] }[] = [
  {
    label: 'Answer buttons',
    fields: [
      segments('look.buttonStyle', 'Fill', ['solid', 'outline', 'ghost', 'gradient'], 'solid'),
      segments('look.buttonSize', 'Size', ['s', 'm', 'l'], 'm'),
      {
        path: 'look.buttonHover',
        label: 'On hover',
        kind: 'select',
        choices: [{ label: 'Theme default', value: null }, ...choices(['darken', 'lighten', 'lift', 'none'])]
      },
      slider('structure.buttonBorderRadius', 'Corner radius', 40),
      slider('structure.answerButtonSpacing', 'Gap between', 40),
      {
        path: 'fontSize.buttonFontWeight',
        label: 'Weight',
        kind: 'select',
        choices: [{ label: 'Theme default', value: null }, ...weights]
      },
      slider('structure.buttonLetterSpacing', 'Letter spacing', 0.3, '0em', 'em', -0.1, 0.01),
      {
        path: 'look.buttonFullWidth',
        label: 'Width',
        kind: 'select',
        choices: [
          { label: 'Theme default', value: null },
          { label: 'Full width', value: true },
          { label: 'Fit to text', value: false }
        ]
      },
      { path: 'look.buttonUppercase', label: 'Uppercase', kind: 'toggle', fallback: false }
    ]
  },
  {
    label: 'Submit buttons',
    fields: [
      {
        path: 'look.submitButtonStyle',
        inheritFrom: 'look.buttonStyle',
        label: 'Fill',
        kind: 'select',
        choices: [
          { label: 'Same as answer buttons', value: null },
          ...choices(['solid', 'outline', 'ghost', 'gradient'])
        ]
      },
      {
        path: 'look.submitButtonSize',
        inheritFrom: 'look.buttonSize',
        label: 'Size',
        kind: 'select',
        choices: [{ label: 'Same as answer buttons', value: null }, ...choices(['s', 'm', 'l'])]
      },
      {
        path: 'look.submitButtonHover',
        inheritFrom: 'look.buttonHover',
        label: 'On hover',
        kind: 'select',
        choices: [
          { label: 'Same as answer buttons', value: null },
          { label: 'Theme default', value: 'theme' },
          ...choices(['darken', 'lighten', 'lift', 'none'])
        ]
      },
      {
        ...slider('structure.submitButtonBorderRadius', 'Corner radius', 40, null),
        inheritFrom: 'structure.buttonBorderRadius'
      },
      {
        path: 'fontSize.submitButtonFontWeight',
        inheritFrom: 'fontSize.buttonFontWeight',
        label: 'Weight',
        kind: 'select',
        choices: [
          { label: 'Same as answer buttons', value: null },
          { label: 'Theme default', value: 'theme' },
          ...weights
        ]
      },
      {
        ...slider('structure.submitButtonLetterSpacing', 'Letter spacing', 0.3, null, 'em', -0.1, 0.01),
        inheritFrom: 'structure.buttonLetterSpacing'
      },
      {
        path: 'look.submitButtonFullWidth',
        inheritFrom: 'look.buttonFullWidth',
        label: 'Width',
        kind: 'select',
        choices: [
          { label: 'Same as answer buttons', value: null },
          { label: 'Theme default', value: 'theme' },
          { label: 'Full width', value: true },
          { label: 'Fit to text', value: false }
        ]
      },
      {
        path: 'look.submitButtonUppercase',
        inheritFrom: 'look.buttonUppercase',
        label: 'Uppercase',
        kind: 'select',
        choices: [
          { label: 'Same as answer buttons', value: null },
          { label: 'On', value: true },
          { label: 'Off', value: false }
        ]
      }
    ]
  }
]
export const designSections: DesignSection[] = [
  {
    id: 'colors',
    title: 'Colors',
    fields: [
      ...backgroundFields,
      ...colorGroups.flatMap((group) => group.fields.map((field) => ({ ...field, kind: 'color' as const }))),
      { ...slider('palette.backdropOpacity', 'Opacity', 1, 0.8, '', 0, 0.05), types: backdropTypes }
    ]
  },
  {
    id: 'typography',
    title: 'Typography',
    fields: [
      { path: 'headingFont', label: 'Heading font', kind: 'font' },
      { path: 'font', label: 'Body font', kind: 'font' },
      { path: 'fontSize.headingFontWeight', label: 'Heading weight', kind: 'select', choices: weights, fallback: 700 },
      { path: 'fontSize.bodyFontWeight', label: 'Body weight', kind: 'select', choices: weights, fallback: 400 },
      slider('fontSize.lineHeight', 'Line height', 3, 1.5, '', 1, 0.1),
      ...sizeFields
    ]
  },
  {
    id: 'buttons',
    title: 'Buttons',
    fields: buttonGroups.flatMap((group) => group.fields)
  },
  {
    id: 'fields',
    title: 'Form fields',
    fields: [
      segments('look.fieldStyle', 'Style', ['boxed', 'underlined', 'filled'], 'boxed'),
      {
        ...segments('look.labelPosition', 'Labels', ['above', 'inside', 'hidden']),
        help: 'Theme default until you choose a label position.'
      },
      slider('structure.fieldBorderRadius', 'Corner radius', 40),
      slider('structure.formFieldSpacing', 'Gap between', 40)
    ]
  },
  {
    id: 'layout',
    title: 'Layout & spacing',
    fields: [
      { ...segments('look.density', 'Density', ['compact', 'comfortable', 'spacious'], 'comfortable'), types: ['bar'] },
      {
        path: 'look.density',
        label: 'Density',
        kind: 'select',
        choices: choices(['compact', 'comfortable', 'spacious', 'custom']),
        fallback: 'comfortable',
        types: spacedTypes
      },
      ...customSpacingFields,
      {
        ...slider('structure.widgetWidth', 'Width', 1200, '400px', 'px', 200, 10),
        types: ['modal', 'toaster', 'inline', 'takeover']
      },
      { ...segments('look.imagePosition', 'Image position', ['top', 'left', 'background']), imageTheme: true },
      { ...slider('structure.imageHeight', 'Image height', 600, '200px', 'px', 40, 10), imageTheme: true }
    ]
  },
  {
    id: 'container',
    title: 'Container',
    fields: [
      slider('structure.containerBorderRadius', 'Corner radius', 40),
      slider('structure.containerBorderWidth', 'Border width', 10),
      segments('look.borderStyle', 'Border style', ['solid', 'dashed', 'dotted'], 'solid'),
      {
        path: 'style.shadowSize',
        label: 'Shadow',
        kind: 'segments',
        types: ['modal', 'takeover', 'toaster', 'bar'],
        choices: [
          { label: 'None', value: 'none' },
          { label: 'S', value: 'sm' },
          { label: 'M', value: 'md' },
          { label: 'L', value: 'lg' },
          { label: 'XL', value: 'xl' },
          { label: '2XL', value: '2xl' }
        ]
      },
      { ...slider('structure.backdropBlur', 'Backdrop blur', 30), types: backdropTypes }
    ]
  },
  {
    id: 'header',
    title: 'Header & progress bar',
    fields: [
      { path: 'headerInfo.isEnabled', label: 'Show a header', kind: 'toggle', types: headerTypes, fallback: false },
      {
        path: 'headerInfo.style',
        label: 'Alignment',
        kind: 'segments',
        choices: [
          { label: 'Left', value: 'left' },
          { label: 'Centered', value: 'center' },
          { label: 'Right', value: 'right' }
        ],
        types: headerTypes,
        when: ['headerInfo.isEnabled', true]
      },
      {
        path: 'headerInfo.imageUrl',
        label: 'Image',
        kind: 'image',
        types: headerTypes,
        when: ['headerInfo.isEnabled', true]
      },
      {
        path: 'headerInfo.title',
        label: 'Title',
        kind: 'text',
        types: headerTypes,
        when: ['headerInfo.isEnabled', true]
      },
      {
        path: 'headerInfo.subtitle',
        label: 'Subtitle',
        kind: 'text',
        types: headerTypes,
        when: ['headerInfo.isEnabled', true]
      },
      {
        path: 'progressBar.isEnabled',
        label: 'Show a progress bar',
        kind: 'toggle',
        types: headerTypes,
        fallback: false
      },
      {
        path: 'progressBar.baseColor',
        label: 'Base color',
        kind: 'color',
        fallback: '#60a5fa',
        types: headerTypes,
        help: 'The gradient is built from this'
      }
    ]
  },
  {
    id: 'motion',
    title: 'Motion',
    fields: [
      segments('look.entrance', 'Entrance', ['none', 'fade', 'slide', 'scale'], 'slide'),
      {
        path: 'look.stepTransition',
        label: 'Step transition',
        kind: 'select',
        choices: stepTransitionChoices,
        fallback: 'slideLeft'
      },
      {
        path: 'look.stepTransitionFade',
        label: 'Fade during step transitions',
        kind: 'toggle',
        fallback: true
      },
      {
        ...segments('look.entranceSpeed', 'Speed', ['slow', 'normal', 'fast'], 'normal'),
        help: 'Applies to the entrance and step transitions.'
      },
      {
        path: 'buttonAnimationTrigger',
        label: 'Draw attention to the submit button',
        kind: 'select',
        choices: [
          { label: "Don't animate", value: '' },
          { label: 'Once after 5 seconds', value: 'once_5s' },
          { label: 'Every 5 seconds', value: 'infinite' }
        ]
      }
    ]
  },
  {
    id: 'close',
    title: 'Close, back, skip & badge',
    fields: [
      {
        ...segments('look.closeButtonStyle', 'Close button', ['icon', 'circle', 'text'], 'icon'),
        types: ['modal', 'takeover', 'toaster', 'bar']
      },
      {
        ...segments('look.closeButtonPosition', 'Position', ['left', 'right'], 'right'),
        types: ['modal', 'takeover', 'toaster', 'bar']
      },
      {
        path: 'look.backButtonStyle',
        label: 'Back button',
        kind: 'segments',
        choices: [
          { label: 'Text link', value: 'link' },
          { label: 'Button', value: 'button' },
          { label: 'Arrow icon', value: 'icon' }
        ],
        fallback: 'link',
        help: 'Turn Back on or off in Display. The arrow icon keeps your Back label for screen readers.'
      },
      {
        path: 'look.skipButtonStyle',
        label: 'Skip button',
        kind: 'segments',
        choices: [
          { label: 'Text link', value: 'link' },
          { label: 'Button', value: 'button' }
        ],
        fallback: 'link',
        help: 'Shown on optional questions. Skip sits beside Next when Back is available.'
      }
    ]
  },
  { id: 'advanced', title: 'Advanced', fields: [{ path: 'customCss', label: 'Custom CSS', kind: 'css' }] }
]

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}
export function readPath(object: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((value, key) => (record(value) ? value[key] : undefined), object)
}
function writePath(object: Record<string, unknown>, path: string, value: unknown) {
  const keys = path.split('.')
  const last = keys.pop()
  if (!last) {
    return
  }
  let target = object
  for (const key of keys) {
    const existing = target[key]
    if (record(existing)) {
      target = existing
    } else {
      const child: Record<string, unknown> = {}
      target[key] = child
      target = child
    }
  }
  target[last] = value
}
const inheritingButtonPaths: Record<string, true> = Object.fromEntries(
  buttonGroups.flatMap((group) =>
    group.fields.filter((field) => field.inheritFrom).map((field) => [field.path, true as const])
  )
)
export function canOverride(type: WidgetType, path: string): boolean {
  return (
    path.startsWith('fontSize.') ||
    (path.startsWith('structure.') && !(type === 'inline' && path === 'structure.widgetWidth')) ||
    [
      'look.density',
      'look.buttonSize',
      'look.buttonFullWidth',
      'look.submitButtonSize',
      'look.submitButtonFullWidth'
    ].includes(path)
  )
}
export function hasOverride(options: Options, device: PreviewDevice, path: string): boolean {
  if (device === 'desktop') {
    return false
  }
  const value = readPath(options, `overrides.${device}.${path}`)
  return value !== undefined && (value !== null || inheritingButtonPaths[path] === true)
}
export function readOption(options: Options, device: PreviewDevice, path: string): unknown {
  return hasOverride(options, device, path) ? readPath(options, `overrides.${device}.${path}`) : readPath(options, path)
}
export function writeOption(
  options: Options,
  type: WidgetType,
  device: PreviewDevice,
  scheme: PreviewScheme,
  path: string,
  value: Value
): boolean {
  if (device !== 'desktop') {
    if (!canOverride(type, path)) {
      return false
    }
    writePath(options, `overrides.${device}.${path}`, value)
    return true
  }
  if (path.startsWith('palette.') && scheme === 'dark') {
    writePath(options, path.replace('palette.', 'paletteDark.'), value)
    options.darkMode = 'auto'
  } else {
    for (const group of ['headerInfo', 'progressBar']) {
      const defaults = readPath(defaultsByType[type], group)
      if (path.startsWith(`${group}.`) && !readPath(options, group) && record(defaults)) {
        writePath(options, group, { ...defaults })
      }
    }
    writePath(options, path, path === 'headerInfo.imageUrl' && value === null ? '' : value)
  }
  return true
}
export function clearOverride(options: Options, device: PreviewDevice, path: string) {
  if (device === 'desktop') {
    return
  }
  const keys = `overrides.${device}.${path}`.split('.')
  const last = keys.pop()
  const parent = readPath(options, keys.join('.'))
  if (last && record(parent)) {
    delete parent[last]
  }
}
const presetPaths = {
  headline: 'headlineFontSize',
  description: 'descriptionFontSize',
  answers: 'answerButtonFontSize',
  formFields: 'formFieldFontSize',
  submitButton: 'submitButtonFontSize',
  headerTitle: 'headerTitleFontSize',
  headerSubtitle: 'headerSubtitleFontSize'
} as const
export function applyTypeScale(options: Options, type: WidgetType, device: PreviewDevice, scale: 's' | 'm' | 'l') {
  const preset = TYPE_SCALE_PRESETS[scale]
  for (const key of Object.keys(presetPaths) as (keyof typeof presetPaths)[]) {
    writeOption(options, type, device, 'light', `fontSize.${presetPaths[key]}`, preset[key])
  }
  if (device === 'desktop') {
    options.typeScale = scale
  }
}
// Unstyled widgets get no RightMessage visual CSS, so only markup and behaviour options still apply. Hiding a field
// never clears its stored value: switching back to a visual theme restores the earlier design.
const UNSTYLED_FIELDS: Record<string, true> = {
  'headerInfo.isEnabled': true,
  'headerInfo.imageUrl': true,
  'headerInfo.title': true,
  'headerInfo.subtitle': true,
  'progressBar.isEnabled': true,
  'look.entrance': true,
  'look.stepTransition': true,
  'look.stepTransitionFade': true,
  'look.entranceSpeed': true,
  'look.closeButtonStyle': true,
  'look.closeButtonPosition': true,
  'look.backButtonStyle': true,
  'look.skipButtonStyle': true
}
export function visibleSections(type: WidgetType, options: Options): DesignSection[] {
  return options.theme === 'unstyled'
    ? designSections.filter((section) => section.fields.some((field) => visibleField(field, type, options)))
    : designSections
}
export function visibleField(
  field: DesignField,
  type: WidgetType,
  options: Options,
  device: PreviewDevice = 'desktop'
): boolean {
  if (field.types && !field.types.includes(type)) {
    return false
  }
  if (field.imageTheme && !IMAGE_THEMES.includes(options.theme)) {
    return false
  }
  if (options.theme === 'unstyled' && UNSTYLED_FIELDS[field.path] !== true) {
    return false
  }
  if (
    field.path === 'look.stepTransitionFade' &&
    !['slideLeft', 'slideUp', 'slideDown', 'zoom'].includes(options.look?.stepTransition ?? 'slideLeft')
  ) {
    return false
  }
  return [field.when, field.alsoWhen].every(
    (condition) =>
      !condition ||
      (readOption(options, device, condition[0]) ?? (condition[0] === 'look.backgroundType' ? 'color' : undefined)) ===
        condition[1]
  )
}
export function widthLabel(type: WidgetType): string {
  return type === 'modal' ? 'Popup width' : type === 'toaster' ? 'Toaster width' : 'Form max width'
}
const paletteFallback: Record<string, string> = {
  backgroundGradientFrom: 'backgroundColor',
  backgroundGradientTo: 'backgroundColor',
  answerButtonColor: 'contrastColor',
  textOnAnswerButtonColor: 'textOnContrastColor',
  linkColor: 'contrastColor',
  questionHeadingColor: 'textOnBackgroundColor',
  questionTextColor: 'textOnBackgroundColor',
  offerHeadingColor: 'textOnBackgroundColor',
  offerTextColor: 'textOnBackgroundColor',
  headerTitleColor: 'textOnBackgroundColor',
  headerSubtitleColor: 'textOnBackgroundColor'
}
export function colorValue(options: Options, type: WidgetType, scheme: PreviewScheme, key: string): string {
  const dark = scheme === 'dark' ? readPath(options, `paletteDark.${key}`) : undefined
  const light = readPath(options, `palette.${key}`)
  if (typeof dark === 'string') {
    return dark
  }
  if (
    scheme === 'dark' &&
    key === 'formFieldTextColor' &&
    typeof readPath(options, 'paletteDark.formFieldColor') === 'string'
  ) {
    return colorValue(options, type, scheme, 'textOnBackgroundColor')
  }
  // Matches serializeWidgetCss: a dark text color replaces light-only step text colors.
  if (
    scheme === 'dark' &&
    paletteFallback[key] === 'textOnBackgroundColor' &&
    typeof readPath(options, 'paletteDark.textOnBackgroundColor') === 'string'
  ) {
    return colorValue(options, type, scheme, 'textOnBackgroundColor')
  }
  if (typeof light === 'string') {
    return light
  }
  const fallback = paletteFallback[key]
  if (fallback) {
    return colorValue(options, type, scheme, fallback)
  }
  const defaults = readPath(defaultsByType[type], `palette.${key}`)
  if (typeof defaults === 'string') {
    return defaults
  }
  const colors: Record<string, string> = {
    formFieldColor: '#ffffff',
    formFieldTextColor: '#000000',
    formFieldBorderColor: '#d8dee4',
    placeholderColor: '#979797',
    backdropColor: '#000000'
  }
  return colors[key] ?? '#000000'
}
export function fieldValue(
  options: Options,
  type: WidgetType,
  device: PreviewDevice,
  scheme: PreviewScheme,
  field: DesignField
): Value {
  if (field.kind === 'color' && field.path.startsWith('palette.')) {
    return colorValue(options, type, scheme, field.path.slice(8))
  }
  const dark =
    scheme === 'dark' && field.path.startsWith('palette.')
      ? readPath(options, field.path.replace('palette.', 'paletteDark.'))
      : undefined
  const value =
    dark ??
    readOption(options, device, field.path) ??
    field.fallback ??
    field.typeFallback?.[type] ??
    readPath(defaultsByType[type], field.path)
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' ? value : null
}
const title = (value: unknown, fallback = 'Theme default'): string =>
  typeof value === 'string' && value ? value.charAt(0).toUpperCase() + value.slice(1) : fallback
export function sectionSummary(
  id: string,
  options: Options,
  type: WidgetType,
  device: PreviewDevice = 'desktop'
): string {
  const get = (path: string) => readOption(options, device, path)
  const text = (path: string, fallback: string) => {
    const value = get(path)
    return typeof value === 'string' || typeof value === 'number' ? String(value) : fallback
  }
  switch (id) {
    case 'colors':
      return `${(backdropTypes.includes(type) ? 17 : 16) + (get('look.backgroundType') === 'gradient' ? 1 : 0) + (headerTypes.includes(type) && get('headerInfo.isEnabled') === true ? 2 : 0)} colors${options.darkMode === 'auto' ? ' · light & dark' : ''}`
    case 'typography':
      return `${typeof options.font === 'string' && options.font ? options.font.replace('google*', '') : 'Same as your site'} · ${text('fontSize.headlineFontSize', 'Theme sizes')}`
    case 'buttons': {
      const customized = buttonGroups.some((group) =>
        group.fields.some((field) => field.inheritFrom && get(field.path) !== null && get(field.path) !== undefined)
      )
      return `Answers: ${title(get('look.buttonStyle'), 'Solid')} · ${title(get('look.buttonSize'), 'M')} · Submit: ${customized ? 'Customized' : 'Same as answers'}`
    }
    case 'fields':
      return `${title(get('look.fieldStyle'), 'Boxed')} · ${title(get('look.labelPosition'), 'Theme labels')}`
    case 'layout':
      return `${title(get('look.density'), 'Comfortable')}${type !== 'bar' ? ` · ${text('structure.widgetWidth', 'Auto width')}` : ''}`
    case 'container':
      return `${text('structure.containerBorderRadius', '0px')} corners · ${text('structure.containerBorderWidth', '0px')} ${title(get('look.borderStyle'), 'Solid').toLowerCase()} border`
    case 'header':
      return headerTypes.includes(type)
        ? `${get('headerInfo.isEnabled') ? 'Header on' : 'Header off'} · ${get('progressBar.isEnabled') ? 'progress on' : 'progress off'}`
        : 'Not available for this widget type'
    case 'motion': {
      const entrance = get('look.entrance') ?? 'slide'
      const transition = get('look.stepTransition') ?? 'slideLeft'
      const transitionLabel = stepTransitionChoices.find((choice) => choice.value === transition)?.label
      return `${entrance === 'slide' ? 'Slide in' : title(entrance)} · ${transitionLabel} · ${title(get('look.entranceSpeed'), 'Normal')}`
    }
    case 'close':
      return `${type === 'inline' ? 'No close button' : title(get('look.closeButtonStyle'), 'Icon')} · ${title(get('look.backButtonStyle'), 'Link')} back · ${title(get('look.skipButtonStyle'), 'Link')} skip`
    case 'advanced':
      return typeof options.customCss === 'string' && options.customCss.trim() ? 'Custom CSS added' : 'No custom CSS'
    default:
      return ''
  }
}
